export const workspaceNavigationRequestEvent = 'yrp:workspace-navigation-request';

/**
 * What kind of move is being asked for: a workspace tab (or other in-app
 * link) to a known href, or the top bar's Back / Forward arrows, whose
 * destination is the browser's previous or next entry (no href known).
 */
export type WorkspaceNavigationKind = 'back' | 'forward' | 'href';

export interface WorkspaceNavigationRequest {
  kind: WorkspaceNavigationKind;
  /** The destination when it is known (`kind: 'href'`); null for Back/Forward. */
  href: string | null;
  resume: () => void;
}

export type WorkspaceNavigationRequestEvent = CustomEvent<WorkspaceNavigationRequest>;

export interface WorkspaceNavigationRequestInput {
  kind: WorkspaceNavigationKind;
  href: string | null;
  /** Called by a listener that held the move and now lets it go ahead (optional). */
  resume?: () => void;
}

/**
 * Ask open work (today: MailComposeModal while sending or uploading) whether
 * the dashboard may move away. Returns true when a listener refused
 * (`preventDefault`), in which case the caller must not navigate.
 *
 *   requestWorkspaceNavigation({ kind: 'back', href: null })
 *   requestWorkspaceNavigation({ kind: 'href', href, resume })
 *   requestWorkspaceNavigation(href, resume)   // older form, same as kind 'href'
 */
export function requestWorkspaceNavigation(request: WorkspaceNavigationRequestInput): boolean;
export function requestWorkspaceNavigation(href: string, resume: () => void): boolean;
export function requestWorkspaceNavigation(
  requestOrHref: WorkspaceNavigationRequestInput | string,
  legacyResume?: () => void,
): boolean {
  const detail: WorkspaceNavigationRequest =
    typeof requestOrHref === 'string'
      ? { kind: 'href', href: requestOrHref, resume: legacyResume ?? (() => {}) }
      : { kind: requestOrHref.kind, href: requestOrHref.href, resume: requestOrHref.resume ?? (() => {}) };
  const event = new CustomEvent<WorkspaceNavigationRequest>(workspaceNavigationRequestEvent, {
    cancelable: true,
    detail,
  });
  window.dispatchEvent(event);
  return event.defaultPrevented;
}
